import { useCallback, useEffect } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

import {
  UseMutateAsyncFunction,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { dequal } from 'dequal';

import { TableView } from '@components/table';
import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';
import { storage } from '@utils/browser';
import { minutes } from '@utils/milliseconds';

import { useWebSocket } from 'api/hooks';
import { handleError } from 'api/query-client';
import { removeDebugToken } from 'api/request';
import { routePaths, travellersListPath } from 'ui/authenticated/navigation';

import { isPlanPaused } from './credits';
import { getLoginErrors, LoginErrorBody, LoginErrors } from './login-errors';
import {
  CompanyUserOptions,
  createUserApp,
  CurrentUser,
  deleteUserApp,
  disconnectGoogleDrive,
  getCurrentUser,
  getGoogleDriveConnectInfo,
  loadUserApps,
  login,
  loginAzure,
  logout,
  switchCompany,
  updateUserApp,
  updateUserOptions,
  UserApp,
  UserAppResponse,
} from './requests';

const ORG_SELECTION_KEY = 'needs-org-selection';

export const setNeedsOrgSelection = (flow: 'signin' | 'signup') => {
  sessionStorage.setItem(ORG_SELECTION_KEY, flow);
};

export const getOrgSelectionFlow = (): 'signin' | 'signup' | null => {
  const value = sessionStorage.getItem(ORG_SELECTION_KEY);
  return value === 'signin' || value === 'signup' ? value : null;
};

export const clearOrgSelection = () => {
  sessionStorage.removeItem(ORG_SELECTION_KEY);
};

const currentUserQueryKey = ['authentication', 'currentUser'];
const googleDriveQueryKey = ['google-drive', 'connect-info'];

export const useInvalidateGoogleDrive = () => {
  const queryClient = useQueryClient();
  return useCallback(() => queryClient.invalidateQueries(googleDriveQueryKey), [queryClient]);
};

export const useGoogleDriveConnection = () => {
  return useQuery({
    queryKey: googleDriveQueryKey,
    queryFn: getGoogleDriveConnectInfo,
  });
};

export const useDisconnectGoogleDrive = () => {
  const invalidateGoogleDrive = useInvalidateGoogleDrive();
  return useMutation({
    mutationFn: disconnectGoogleDrive,
    onSuccess: invalidateGoogleDrive,
  });
};

export const useCurrentUserQuery = () => {
  return useQuery({
    queryKey: currentUserQueryKey,
    queryFn: getCurrentUser,
    staleTime: Infinity,
    retryOnMount: false,
    refetchInterval: minutes(10),
    refetchOnWindowFocus: 'always',
  });
};

export const useCurrentUser = () => {
  const query = useCurrentUserQuery();

  return query.data;
};

export const useCurrentCompany = () => {
  const currentUser = useCurrentUser();
  return currentUser?.company;
};

export const useIsOutOfCredits = () => {
  const currentUser = useCurrentUser();

  return isPlanPaused(currentUser?.plan);
};

export const usePlan = () => {
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  const company = useCurrentCompany();
  const enabled = Boolean(company?.shortId);
  useWebSocket(`plans/${company?.shortId}/`, invalidateCurrentUser, {
    throttleTime: 10000,
    enabled,
  });
  const currentUser = useCurrentUser();
  return currentUser?.plan;
};

export const useTableView = (tableName: string) => {
  const { value, update } = useUserOption(`tableViewFor${tableName}`, null);
  return {
    tableView: value,
    setTableView: update.mutateAsync as UseMutateAsyncFunction<any, unknown, TableView, unknown>,
  };
};

export const useUserOption = <K extends keyof CompanyUserOptions>(key: K, defaultValue: any) => {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const { invalidateCurrentUser } = useInvalidateAuthentication();
  const update = useMutation(
    (value: CompanyUserOptions[K]) => {
      queryClient.setQueryData(currentUserQueryKey, (current: CurrentUser | undefined) => {
        if (current?.options) {
          current.options[key] = value;
          return { ...current };
        }
      });
      return updateUserOptions({ key, value });
    },
    {
      onSuccess: invalidateCurrentUser,
    },
  );
  return { update, value: currentUser?.options?.[key] ?? defaultValue };
};

export type { LoginErrors };

type LoginOptions = {
  /**
   * Runs once the session exists, while the signed-in user is fetched. The app swaps to the
   * signed-in screens when both are done.
   */
  beforeRedirect?: () => Promise<void>;
};

export const useLogin = (
  setErrors: (errors: LoginErrors) => void,
  path?: string,
  { beforeRedirect }: LoginOptions = {},
) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  return useMutation({
    mutationFn: login,
    onSuccess: async () => {
      setNeedsOrgSelection('signin');
      const [currentUser] = await Promise.all([getCurrentUser(), beforeRedirect?.()]);
      // Storing the user swaps in the signed-in screens. Wait for this mutation to settle
      // first, so it doesn't report back to a sign-in page that is already gone.
      setTimeout(() => {
        queryClient.setQueryData(currentUserQueryKey, currentUser);
        if (path) {
          setTimeout(() => {
            navigate(path, { state: { from: location } });
          });
        }
      });
    },
    onError: (error: AxiosError<LoginErrorBody>) => {
      handleError(() => {
        setErrors(getLoginErrors(error?.response?.data, t('unknownError')));
      });
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      clearOrgSelection();
      removeDebugToken();
      queryClient.resetQueries({ queryKey: currentUserQueryKey, exact: true });
      queryClient.removeQueries({
        predicate: (query) => {
          return !dequal(query.queryKey, currentUserQueryKey);
        },
      });
    },
  });
};

export const useLoginAzure = () => {
  return useMutation({
    mutationFn: loginAzure,
  });
};

const userAppsQueryKey = ['user-apps'];

export const useUserApps = () => {
  return useQuery({
    queryKey: userAppsQueryKey,
    queryFn: () => loadUserApps(),
  });
};

export const useInvalidateAuthentication = () => {
  const queryClient = useQueryClient();
  const invalidateCurrentUser = () => queryClient.invalidateQueries(currentUserQueryKey);
  const invalidateUserApps = () => queryClient.invalidateQueries(userAppsQueryKey);

  return { invalidateCurrentUser, invalidateUserApps };
};

export const useCreateUserApp = (
  options?: UseMutationOptions<UserAppResponse, unknown, Omit<UserApp, 'clientId'>>,
) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateUserApps } = useInvalidateAuthentication();

  return useMutation({
    ...options,
    mutationFn: createUserApp,
    onSuccess: (...data) => {
      invalidateUserApps();
      showSuccess(t('userAppCreated'));
      options?.onSuccess?.(...data);
    },
    onError: () => {
      handleError(t('userAppNotCreated'));
    },
  });
};

export const useUpdateUserApp = (options?: UseMutationOptions<UserApp, unknown, UserApp>) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateUserApps } = useInvalidateAuthentication();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: updateUserApp,
    onSuccess: (...data) => {
      invalidateUserApps();
      queryClient.invalidateQueries(['user-app']);
      showSuccess(t('userAppUpdated'));
      options?.onSuccess?.(...data);
    },
    onError: () => {
      handleError(t('userAppNotUpdated'));
    },
  });
};

export const useDeleteUserApp = (options?: UseMutationOptions<void, unknown, unknown>) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateUserApps } = useInvalidateAuthentication();

  return useMutation(deleteUserApp, {
    ...options,
    onSuccess(...data) {
      invalidateUserApps();
      showSuccess(t('userAppDeleted'));
      options?.onSuccess?.(...data);
    },
    onError: () => {
      handleError(t('userAppNotDeleted'));
    },
  });
};

export const useSwitchCompany = () => {
  const queryClient = useQueryClient();
  const { t } = useScopedTranslate(locale);
  const navigate = useNavigate();

  let switchTimeout: NodeJS.Timeout;

  useEffect(() => {
    return () => {
      clearTimeout(switchTimeout);
    };
  });
  return useMutation(switchCompany, {
    onSuccess(_, variables) {
      clearOrgSelection();
      queryClient.resetQueries({ queryKey: currentUserQueryKey, exact: true });
      queryClient.removeQueries({
        predicate: (query) => {
          return !dequal(query.queryKey, currentUserQueryKey);
        },
      });
      const currentWorkspace = storage.get('current-workspace-' + variables.shortId);
      const defaultRoute = currentWorkspace
        ? generatePath(routePaths.workspaceJourneyList, {
            workspaceId: currentWorkspace,
          })
        : travellersListPath;
      navigate(defaultRoute, { replace: true });

      switchTimeout = setTimeout(() => {
        // Timeout so it transitions over to the new company before showing,
        // that way it stops freezing on screen when the toaster component unmounts.
        showSuccess(t('successfullySwitched', { companyName: variables.name }));
      }, 300);
    },
    onError: () => {
      handleError(t('switchUnsuccessful'));
    },
  });
};

const locale = {
  en: {
    unknownError: 'Unknown error',
    userAppCreated: 'API key created',
    userAppNotCreated: 'Unable to create API key',
    userAppUpdated: 'API key updated',
    userAppNotUpdated: 'Unable to update API key',
    userAppDeleted: 'API key removed',
    userAppNotDeleted: 'Unable to remove API key',
    successfullySwitched: 'Successfully switched company to {companyName}',
    switchUnsuccessful: 'Switching companies was unsuccessful',
  },
};
